export interface OAuthCallbackResult {
  type: string;
  error?: string;
  state?: string;
}

const oauthCallbackFlowModeKey = (providerName: string, state: string): string =>
  `getea-oauth-flow:${providerName.toLowerCase()}:${state}`;

interface OAuthRedirectFlow {
  mode: "redirect";
  returnPath: string | null;
}

function safeReturnPath(returnPath: string | null | undefined): string | null {
  if (!returnPath?.startsWith("/") || returnPath.startsWith("//")) return null;
  return returnPath;
}

function readOAuthRedirectFlow(
  providerName: string,
  state: string,
): OAuthRedirectFlow | null {
  try {
    const stored = sessionStorage.getItem(oauthCallbackFlowModeKey(providerName, state));
    if (stored === "redirect") return { mode: "redirect", returnPath: null };
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<OAuthRedirectFlow> | null;
    if (parsed?.mode !== "redirect") return null;
    return {
      mode: "redirect",
      returnPath: safeReturnPath(parsed.returnPath),
    };
  } catch {
    return null;
  }
}

export function oauthCallbackChannelName(providerName: string, state: string): string {
  return `getea-oauth:${providerName.toLowerCase()}:${state}`;
}

export function broadcastOAuthCallbackResult(
  providerName: string,
  state: string,
  result: OAuthCallbackResult,
): void {
  if (typeof BroadcastChannel === "undefined") return;

  try {
    const channel = new BroadcastChannel(oauthCallbackChannelName(providerName, state));
    channel.postMessage(result);
    channel.close();
  } catch {
    // Backend status polling remains the fallback when cross-tab messaging is
    // unavailable or blocked by the browser.
  }
}

export function markOAuthRedirectFlow(
  providerName: string,
  state: string,
  returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`,
): void {
  try {
    const flow: OAuthRedirectFlow = {
      mode: "redirect",
      returnPath: safeReturnPath(returnPath),
    };
    sessionStorage.setItem(
      oauthCallbackFlowModeKey(providerName, state),
      JSON.stringify(flow),
    );
  } catch {
    // The callback defaults to popup handling if storage is unavailable. The
    // redirect route still completes server-side and shows an explicit escape.
  }
}

export function isOAuthRedirectFlow(providerName: string, state: string): boolean {
  return readOAuthRedirectFlow(providerName, state) !== null;
}

export function getOAuthRedirectReturnPath(
  providerName: string,
  state: string,
): string | null {
  return readOAuthRedirectFlow(providerName, state)?.returnPath ?? null;
}

export function clearOAuthRedirectFlow(providerName: string, state: string): void {
  try {
    sessionStorage.removeItem(oauthCallbackFlowModeKey(providerName, state));
  } catch {
    // The marker is session-scoped and contains no credentials. If storage is
    // unavailable, there is nothing actionable to clean up.
  }
}
